/*
 * Sección Horas trabajadas: horas por persona por día, semana (lunes a domingo) o mes, pedidas a
 * GET /attendance/horas/ con TODOS los filtros (zona, tienda y empleado van al servidor). Solo suman
 * las jornadas completas; las incompletas se cuentan aparte (spec 4.1 y 4.3, sin horas extra).
 * Si el periodo es un solo día, muestra además las barras de jornada de ese día (con las jornadas
 * que ya cargó el contenedor).
 * Contrato de /horas/ que la UI respeta: con semana/mes, periodo_inicio/periodo_fin son del periodo
 * calendario completo pero el total cubre solo los días dentro del rango (se rotula "parcial"); un
 * grupo con solo jornadas incompletas trae 0 h (se dice "sin jornadas completas"); los tiempos se
 * formatean desde minutos_trabajados (h y min) para no diferir de las filas.
 */
import React, {useEffect, useState} from "react";
import {fetchHours} from "../../../../redux/attendance";
import useRemoteData from "../hooks/useRemoteData";
import StateView from "../components/StateView";
import SectionHeader from "../components/SectionHeader";
import StatTile from "../components/StatTile";
import StatusBadge from "../components/StatusBadge";
import EmployeeName from "../components/EmployeeName";
import Paginator from "../components/Paginator";
import BarraJornada, {TimelineAxis} from "../components/BarraJornada";
import {arrivalStatus, durationLabel, entryList, hoursList, hoursSummary, paginate} from "../utils/rows";
import {buildParams, defaultGrouping, groupLabel} from "../utils/period";

const GROUPS = [
    {key: 'dia', label: 'Por día'},
    {key: 'semana', label: 'Por semana'},
    {key: 'mes', label: 'Por mes'},
];

/** Tiempo de una fila o total: "sin jornadas completas" si no hay minutos pero sí jornadas incompletas. */
const workedLabel = (minutes, incompletas) => (
    minutes > 0 ? durationLabel(minutes) : (incompletas > 0 ? 'sin jornadas completas' : durationLabel(minutes))
);

const Horas = ({rows, status, message, onRetry, filters, range, today, token, canExport, resetKey}) => {
    const [grouping, setGrouping] = useState(defaultGrouping(filters.periodo));
    const [page, setPage] = useState(1);
    useEffect(() => {
        setPage(1);
    }, [resetKey, grouping]);
    useEffect(() => {
        setGrouping(defaultGrouping(filters.periodo));
    }, [filters.periodo]);

    const params = buildParams(filters, range, {agrupar: grouping});
    const hours = useRemoteData(() => fetchHours(token, params), token ? JSON.stringify(params) : null);
    const hourRows = hours.data ? hours.data.datos : [];
    const summary = hoursSummary(hourRows);
    const list = hoursList(hourRows);
    const current = paginate(list, page);
    const maxMinutes = list.reduce((max, row) => Math.max(max, row.minutos_trabajados), 1);
    const totalMinutes = hourRows.reduce((sum, row) => sum + row.minutos_trabajados, 0);
    const singleDay = range.desde === range.hasta;
    // Solo con las jornadas ya cargadas de ESE día (no las del periodo anterior mientras carga).
    const dayRows = singleDay && status === 'ok' ? entryList(rows.filter((row) => row.fecha === range.desde)) : [];
    const isPartial = (row) => grouping !== 'dia' && (row.periodo_inicio < range.desde || row.periodo_fin > range.hasta);

    return (
        <section className="mc-section" aria-labelledby="mc-title-horas">
            <SectionHeader id="mc-title-horas" title="Horas trabajadas" canExport={canExport} token={token} range={range}
                           exportParams={{...params, seccion: 'horas'}}/>
            <div className="mc-toggle" role="group" aria-label="Agrupar horas">
                {GROUPS.map((item) => (
                    <button key={item.key} type="button" aria-pressed={grouping === item.key}
                            className={`mc-toggle__item${grouping === item.key ? ' is-active' : ''}`}
                            onClick={() => setGrouping(item.key)}>
                        {item.label}
                    </button>
                ))}
            </div>
            <StateView status={hours.status} message={hours.message} onRetry={hours.reload} isEmpty={list.length === 0}>
                <div className="mc-tiles">
                    <StatTile label="Horas trabajadas" value={workedLabel(totalMinutes, summary.incompletas)}
                              hint="Solo jornadas completas"/>
                    <StatTile label="Por persona"
                              value={summary.personas ? durationLabel(totalMinutes / summary.personas) : '—'}
                              hint={`${summary.personas} ${summary.personas === 1 ? 'persona' : 'personas'} con horas`}/>
                    <StatTile label="Jornadas completas" value={summary.completas}
                              hint={`${summary.sinAlmuerzo} sin almuerzo marcado`}/>
                    <StatTile label="Incompletas" value={summary.incompletas} tone={summary.incompletas ? 'down' : 'neutral'}
                              hint="No suman horas"/>
                </div>
                {singleDay && status !== 'ok' &&
                    <StateView status={status} message={message} onRetry={onRetry}/>}
                {dayRows.length > 0 &&
                    <div className="mc-panel">
                        <h3 className="mc-panel__title">Jornada del día</h3>
                        <div className="mc-list__head" aria-hidden="true">
                            <span>Persona</span><span>Horas</span><span>Llegada</span><TimelineAxis/>
                        </div>
                        <ul className="mc-list">
                            {dayRows.map((row) => {
                                const arrival = arrivalStatus(row);
                                return (
                                    <li key={`${row.fecha}-${row.id_empleado}`} className="mc-list__item mc-list__item--timeline">
                                        <EmployeeName row={row}/>
                                        <span className="mc-list__time mc-num">{durationLabel(row.minutos_trabajados)}</span>
                                        <StatusBadge tone={arrival.tone} text={arrival.text}/>
                                        <BarraJornada row={row} today={today}/>
                                    </li>
                                );
                            })}
                        </ul>
                    </div>}
                <div className="mc-panel">
                    <h3 className="mc-panel__title">Horas por persona</h3>
                    <ul className="mc-list">
                        {current.items.map((row) => (
                            <li key={`${row.periodo_inicio}-${row.id_empleado}`} className="mc-list__item mc-list__item--hours">
                                <EmployeeName row={row}/>
                                <span className="mc-list__time mc-list__time--wrap">
                                    <span className="mc-num">{workedLabel(row.minutos_trabajados, row.jornadas_incompletas)}</span>
                                    <small>
                                        {groupLabel(row.periodo_inicio, grouping)}
                                        {isPartial(row) ? ' · parcial, dentro del rango' : ''}
                                    </small>
                                </span>
                                <span className="mc-hours">
                                    <span className="mc-hours__bar" aria-hidden="true">
                                        <span style={{width: `${Math.round((row.minutos_trabajados / maxMinutes) * 100)}%`}}/>
                                    </span>
                                    <small className="mc-num">
                                        {row.jornadas_completas} completas
                                        {row.jornadas_incompletas ? ` · ${row.jornadas_incompletas} incompletas` : ''}
                                        {row.jornadas_en_curso ? ` · ${row.jornadas_en_curso} en curso` : ''}
                                    </small>
                                </span>
                            </li>
                        ))}
                    </ul>
                    <Paginator page={current.page} pages={current.pages} onChange={setPage}/>
                </div>
            </StateView>
        </section>
    );
};

export default Horas;
